import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// Import the delete effect from its own module, not the feature barrel: the
// barrel pulls the sibling create/update effects and their lead/opportunity slice
// graph into the calendar, which trips a circular-import init (orderByToArray
// undefined) when this hook is mounted from CalendarPage.
import { deleteActivityEffect } from '@user/features/Activity/effects/deleteActivity.effect'
import type { CalendarEvent } from '../typings'
import { invalidateEventDetail } from './useEventDetail'

// SH-1432: orchestrates edit + delete of an activity from the calendar's event
// detail popover. Owns the edit-modal and delete-confirmation state and routes
// success to the two things the calendar actually controls — the feed refetch
// (`retry`) and the popover hydration cache. It deliberately does NOT try to
// refresh the Activity Summary / timeline: those live behind feature-local
// Activity contexts that the calendar page is not mounted inside, and the one
// global surface (the parent Lead/Opportunity next-action date) is already
// dispatched to Redux inside ActivityForm's submit handler.

type Args = {
  /** The calendar feed refetch seam (useCalendarEvents().retry). */
  retry: () => void
}

export type CalendarActivityActions = {
  // Edit
  editActivityId: number | undefined
  // The parent record of the activity being edited. GET /Activities/{id} does not
  // return it, so it comes from the calendar event the user clicked.
  editEntity:
    | { entityType: 'Lead' | 'Opportunity'; entityId: number }
    | undefined
  isEditOpen: boolean
  onEdit: (event: CalendarEvent) => void
  onEditClose: () => void
  onEditSuccess: () => void
  // Delete
  pendingDeleteEvent: CalendarEvent | null
  isConfirmOpen: boolean
  isDeleting: boolean
  onDelete: (event: CalendarEvent) => void
  onDeleteCancel: () => void
  onDeleteConfirm: () => Promise<void>
}

export function useCalendarActivityActions({
  retry,
}: Args): CalendarActivityActions {
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null)
  const [pendingDeleteEvent, setPendingDeleteEvent] =
    useState<CalendarEvent | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Guard the post-await state writes below: the confirm dialog can be unmounted
  // (page nav) while the DELETE is in flight, and a setState on an unmounted hook
  // is a no-op warning at best. Same pattern as useEditActivity's `mounted` flag.
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  const onEdit = useCallback((event: CalendarEvent) => {
    // No activity id → nothing to edit (markers, or a feed without the id).
    if (event.activityId == null) return
    setEditEvent(event)
  }, [])

  const onEditClose = useCallback(() => setEditEvent(null), [])

  const onEditSuccess = useCallback(() => {
    if (editEvent) {
      invalidateEventDetail(editEvent.entityType, editEvent.entityId)
    }
    retry()
    setEditEvent(null)
  }, [editEvent, retry])

  const onDelete = useCallback((event: CalendarEvent) => {
    if (event.activityId == null) return
    setPendingDeleteEvent(event)
  }, [])

  // Once the DELETE is on the wire it cannot be recalled, so Cancel / Escape /
  // backdrop must not dismiss the dialog as if it could — the activity would be
  // deleted anyway, after the user believed they had backed out. Ignore them
  // until the request settles; onDeleteConfirm closes the dialog itself.
  const onDeleteCancel = useCallback(() => {
    if (isDeleting) return
    setPendingDeleteEvent(null)
  }, [isDeleting])

  const onDeleteConfirm = useCallback(async () => {
    const target = pendingDeleteEvent
    if (!target || target.activityId == null) return

    setIsDeleting(true)
    try {
      // deleteActivityEffect performs the DELETE and toasts on failure; it
      // returns deleteActivity(id) on success and deleteActivity(-1) on failure.
      const action = await deleteActivityEffect(target.activityId)
      // Unmounted mid-request → skip all follow-up work (state + side effects).
      if (!mountedRef.current) return
      if (action.payload !== -1) {
        // Success: drop the stale hydration for the linked record and refetch
        // the calendar so the event disappears (REQ-7 / AC-7.2).
        invalidateEventDetail(target.entityType, target.entityId)
        retry()
      }
      // Failure (EC-1): the effect already toasted; the activity remains on
      // every surface, so no refetch and no cache change — just close the dialog.
    } finally {
      if (mountedRef.current) {
        setIsDeleting(false)
        setPendingDeleteEvent(null)
      }
    }
  }, [pendingDeleteEvent, retry])

  // Memoise the returned bag so its identity is stable across renders that don't
  // change the underlying state — CalendarPage keys useCallbacks on it, so a fresh
  // object every render would defeat that memoisation and re-prop the popover.
  return useMemo(
    () => ({
      editActivityId: editEvent?.activityId,
      // Only Lead/Opportunity can parent an activity (PendingLead cannot), and an
      // unattached activity has no entity at all.
      editEntity:
        editEvent?.entityId != null &&
        (editEvent.entityType === 'Lead' ||
          editEvent.entityType === 'Opportunity')
          ? {
              entityType: editEvent.entityType,
              entityId: editEvent.entityId,
            }
          : undefined,
      isEditOpen: editEvent != null,
      onEdit,
      onEditClose,
      onEditSuccess,
      pendingDeleteEvent,
      isConfirmOpen: pendingDeleteEvent != null,
      isDeleting,
      onDelete,
      onDeleteCancel,
      onDeleteConfirm,
    }),
    [
      editEvent,
      pendingDeleteEvent,
      isDeleting,
      onEdit,
      onEditClose,
      onEditSuccess,
      onDelete,
      onDeleteCancel,
      onDeleteConfirm,
    ],
  )
}
